import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { haptic } from "../platform/telegram";

/**
 * بازخورد ثبت.
 *
 * درس تست کاربر: بدون تأیید دیدنی، کاربر فکر می‌کند دکمه کار نکرده —
 * حتی وقتی داده درست ذخیره شده. و اگر اقدامی چیزی را جابه‌جا کند،
 * باید بشود همان‌جا برش گرداند، نه اینکه کاربر دنبالش بگردد.
 */
interface ToastItem {
  id: number;
  text: string;
  tone: "ok" | "info";
  undo?: () => void;
}

const Ctx = createContext<{
  show: (text: string, opts?: { tone?: "ok" | "info"; undo?: () => void }) => void;
} | null>(null);

let seq = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef<Map<number, number>>(new Map());

  const dismiss = useCallback((id: number) => {
    setItems((xs) => xs.filter((x) => x.id !== id));
    const t = timers.current.get(id);
    if (t) { window.clearTimeout(t); timers.current.delete(id); }
  }, []);

  const show = useCallback((text: string, opts: { tone?: "ok" | "info"; undo?: () => void } = {}) => {
    const id = ++seq;
    haptic(opts.tone === "info" ? "tap" : "success");
    setItems((xs) => [...xs.slice(-2), { id, text, tone: opts.tone ?? "ok", undo: opts.undo }]);
    // اقدام برگشت‌پذیر وقت بیشتری می‌خواهد تا کاربر بتواند تصمیم بگیرد
    timers.current.set(id, window.setTimeout(() => dismiss(id), opts.undo ? 6000 : 2600));
  }, [dismiss]);

  useEffect(() => () => { timers.current.forEach((t) => window.clearTimeout(t)); }, []);

  return (
    <Ctx.Provider value={{ show }}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className="toast" data-tone={t.tone}>
            <span>{t.text}</span>
            {t.undo && (
              <button
                onClick={() => { t.undo?.(); dismiss(t.id); haptic("tap"); }}
              >
                برگردان
              </button>
            )}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useToast باید داخل ToastProvider باشد");
  return c.show;
}
